import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, User, ChevronDown, Instagram, Facebook, Menu, X, Heart } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { catalogAPI } from '../../api';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_291951d3-4c04-4e5d-b3ee-9e43e83189ef/artifacts/g1d3eigk_logo-petals-west-inc.png';

export default function Header() {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState({ products: [], categories: [] });
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const searchRef = useRef(null);
  const accountRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearch(false);
      if (accountRef.current && !accountRef.current.contains(e.target)) setShowAccountMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchQuery.length >= 2) {
        try {
          const res = await catalogAPI.searchSuggestions(searchQuery);
          setSuggestions(res.data.suggestions);
        } catch { setSuggestions({ products: [], categories: [] }); }
      } else {
        setSuggestions({ products: [], categories: [] });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setShowSearch(false);
      setSearchQuery('');
    }
  };

  const cartCount = cart?.item_count || 0;

  return (
    <header>
      {/* Pre-header - Deep muted green */}
      <div data-testid="preheader-bar" className="bg-[#2f3e36] text-white text-[10px] sm:text-xs tracking-[0.22em] uppercase text-center py-2.5">
        <span data-testid="preheader-free-shipping-text">FREE SHIPPING OVER $50</span>
      </div>

      {/* Main Header - White background */}
      <div
        data-testid="site-header"
        className={`sticky top-0 z-50 bg-white transition-shadow duration-200 ${scrolled ? 'shadow-md' : 'border-b border-[#e7e3dd]'}`}
      >
        <div className="pw-container">
          <div className="flex items-center justify-between h-[70px] md:h-[80px]">
            {/* Mobile Menu Button */}
            <button type="button" aria-label={mobileMenu ? 'Close navigation menu' : 'Open navigation menu'} aria-expanded={mobileMenu} className="md:hidden p-2 text-[#2d2d2d]" onClick={() => setMobileMenu(!mobileMenu)}>
              {mobileMenu ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Logo - Real Petals West logo */}
            <Link to="/" data-testid="header-logo" className="flex items-center flex-shrink-0">
              <img 
                src={LOGO_URL} 
                alt="Petals West Inc." 
                className="h-[50px] md:h-[60px] w-auto object-contain"
              />
            </Link>

            {/* Navigation - Center */}
            <nav data-testid="header-nav" className="hidden md:flex items-center gap-0 ml-8">
              <Link data-testid="header-nav-specials" to="/specials" className="text-xs tracking-[0.18em] uppercase text-[#4a4a4a] hover:text-[#2d2d2d] transition-colors px-5 py-2">
                WEEKLY SPECIALS
              </Link>
              <span className="text-[#d0d0d0]">|</span>
              <Link data-testid="header-nav-our-varieties" to="/shop" className="text-xs tracking-[0.18em] uppercase text-[#4a4a4a] hover:text-[#2d2d2d] transition-colors px-5 py-2">
                OUR VARIETIES
              </Link>
              <span className="text-[#d0d0d0]">|</span>
              <Link data-testid="header-nav-about" to="/about" className="text-xs tracking-[0.18em] uppercase text-[#4a4a4a] hover:text-[#2d2d2d] transition-colors px-5 py-2">
                ABOUT US
              </Link>
              <span className="text-[#d0d0d0]">|</span>
              <Link data-testid="header-nav-contact" to="/contact" className="text-xs tracking-[0.18em] uppercase text-[#4a4a4a] hover:text-[#2d2d2d] transition-colors px-5 py-2">
                CONTACT US
              </Link>
            </nav>

            {/* Right Icons - Dark on white */}
            <div className="flex items-center gap-4 md:gap-5">
              {/* Search */}
              <div ref={searchRef} className="relative">
                <button type="button" aria-label="Search products" aria-expanded={showSearch} data-testid="header-search-button" onClick={() => setShowSearch(!showSearch)} className="p-1.5 text-[#6f6a63] hover:text-[#2d2d2d] transition-colors">
                  <Search size={18} />
                </button>
                {showSearch && (
                  <div className="absolute right-0 top-full mt-2 w-72 md:w-96 bg-white text-gray-900 rounded-xl shadow-2xl p-4 z-50 animate-scale-in border border-[#e7e3dd]">
                    <form onSubmit={handleSearch}>
                      <input
                        autoFocus
                        aria-label="Search flowers, colors, and categories"
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search flowers, colors, categories..."
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2f6b4f]"
                      />
                    </form>
                    {(suggestions.products?.length > 0 || suggestions.categories?.length > 0) && (
                      <div className="mt-3 max-h-60 overflow-y-auto">
                        {suggestions.categories?.length > 0 && (
                          <div className="mb-2">
                            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Categories</p>
                            {suggestions.categories.map((c) => (
                              <Link key={c._id} to={`/shop?category=${c.name}`} className="block px-3 py-1.5 text-sm hover:bg-gray-50 rounded" onClick={() => { setShowSearch(false); setSearchQuery(''); }}>
                                {c.name}
                              </Link>
                            ))}
                          </div>
                        )}
                        {suggestions.products?.length > 0 && (
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Products</p>
                            {suggestions.products.map((p) => (
                              <Link key={p._id} to={`/product/${p.slug}`} className="block px-3 py-1.5 text-sm hover:bg-gray-50 rounded" onClick={() => { setShowSearch(false); setSearchQuery(''); }}>
                                {p.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Account */}
              <div ref={accountRef} className="relative">
                <button type="button" aria-label="Open account menu" aria-expanded={showAccountMenu} data-testid="header-account-menu-trigger" onClick={() => setShowAccountMenu(!showAccountMenu)} className="flex items-center gap-1.5 p-1.5 text-[#4a4a4a] hover:text-[#2d2d2d] transition-colors">
                  <User size={18} className="text-[#888]" />
                  <span className="hidden md:inline text-xs tracking-wide text-[#4a4a4a]">My Account</span>
                  <ChevronDown size={13} className="hidden md:inline text-[#888]" />
                </button>
                {showAccountMenu && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white text-gray-900 rounded-xl shadow-2xl py-2 z-50 animate-scale-in border border-[#e7e3dd]">
                    {user ? (
                      <>
                        <div className="px-4 py-2 border-b border-gray-100">
                          <p className="text-xs text-gray-500">Welcome</p>
                          <p className="text-sm font-medium truncate">{user.buyer_name || user.email}</p>
                        </div>
                        <Link to="/account" className="block px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setShowAccountMenu(false)}>My Account</Link>
                        <Link to="/account/orders" className="block px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setShowAccountMenu(false)}>Orders</Link>
                        <Link to="/account/wishlist" className="block px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setShowAccountMenu(false)}>Wishlist</Link>
                        {user.admin_access && (
                          <Link to="/admin" className="block px-4 py-2 text-sm text-[#2f6b4f] font-medium hover:bg-gray-50" onClick={() => setShowAccountMenu(false)}>Admin Panel</Link>
                        )}
                        <button onClick={() => { logout(); setShowAccountMenu(false); navigate('/'); }} className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50">
                          Sign Out
                        </button>
                      </>
                    ) : (
                      <>
                        <Link to="/login" className="block px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setShowAccountMenu(false)}>Sign In</Link>
                        <Link to="/register" className="block px-4 py-2 text-sm hover:bg-gray-50" onClick={() => setShowAccountMenu(false)}>Register</Link>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Cart */}
              <Link to="/cart" aria-label={`Shopping cart, ${cartCount} item${cartCount === 1 ? '' : 's'}`} data-testid="header-cart-button" className="relative p-1.5 text-[#6f6a63] hover:text-[#2d2d2d] transition-colors">
                <ShoppingCart size={18} />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#2f6b4f] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center badge-pop">
                    {cartCount}
                  </span>
                )}
              </Link>

              {/* Socials */}
              <div className="hidden lg:flex items-center gap-2.5 ml-1">
                <a aria-label="Petals West on Instagram" data-testid="header-social-instagram" href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="p-1 text-[#6f6a63] hover:text-[#4a4a4a] transition-colors">
                  <Instagram size={16} />
                </a>
                <a aria-label="Petals West on Facebook" data-testid="header-social-facebook" href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="p-1 text-[#6f6a63] hover:text-[#4a4a4a] transition-colors">
                  <Facebook size={16} />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenu && (
          <div className="md:hidden bg-white border-t border-[#e7e3dd] animate-fade-in-up">
            <nav className="pw-container py-4 space-y-3">
              <Link to="/specials" className="block text-sm tracking-wider uppercase py-2 text-[#4a4a4a]" onClick={() => setMobileMenu(false)}>Weekly Specials</Link>
              <Link to="/shop" className="block text-sm tracking-wider uppercase py-2 text-[#4a4a4a]" onClick={() => setMobileMenu(false)}>Our Varieties</Link>
              <Link to="/about" className="block text-sm tracking-wider uppercase py-2 text-[#4a4a4a]" onClick={() => setMobileMenu(false)}>About Us</Link>
              <Link to="/contact" className="block text-sm tracking-wider uppercase py-2 text-[#4a4a4a]" onClick={() => setMobileMenu(false)}>Contact Us</Link>
              {user && <Link to="/account" className="block text-sm tracking-wider uppercase py-2 text-[#4a4a4a]" onClick={() => setMobileMenu(false)}>My Account</Link>}
              {user?.admin_access && <Link to="/admin" className="block text-sm tracking-wider uppercase py-2 text-[#2f6b4f]" onClick={() => setMobileMenu(false)}>Admin Panel</Link>}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
